/**
 * Dashboard layout selector.
 *
 * Compares `staff.role` against the 7 canonical English codes enforced by
 * migration 0013 + the `staff_role_canonical` DB CHECK constraint. The
 * Vietnamese substring fallbacks the previous version carried (for legacy
 * Appwrite role strings) are gone — those values can no longer be stored.
 *
 * Department `bod` (board of directors) still wins regardless of role, and
 * the `sale` department drives layout when role is blank. The department
 * column itself isn't canonicalised yet, so `"sales"` (legacy plural) is
 * still accepted as a synonym of `"sale"` until M3 data cleanup lands.
 */

import type { Staff } from "@/lib/authv2/store";
import { ROLE } from "@/lib/auth/roles";
import { TECHNICAL_BOARD_DEPARTMENTS } from "@/features/medals/permissions";

export type DashboardLayout =
  | "staff"
  | "sale-staff"
  | "manager"
  // Bàn điều hành (CEO chốt 4a · 5a, 20/10/2026): trưởng cửa hàng có bàn cơ
  // sở, quản lý cụm có bảng các cơ sở trong cụm.
  | "store"
  | "area"
  | "full-access";

const norm = (s?: string | null) => (s ?? "").toLowerCase().trim();

const SALE_DEPTS = new Set(["sale", "sales"]); // sales = legacy plural

function classify(staff: Staff | null): DashboardLayout {
  const role = norm(staff?.role);
  const dept = norm(staff?.department);

  if (dept === "bod") return "full-access";

  switch (role) {
    case ROLE.Admin:
    case ROLE.Director:
    case ROLE.Accounting:
      return "full-access";
    // ROLE.Coordinator ĐÃ RỜI nhánh này 23/08/2026 (quyết định 0050): vai
    // Điều Phối bị khoá nên không còn bố cục cấp quản lý — rơi xuống "staff"
    // ở cuối hàm.
    // ⚠️ Nhánh `dept === "bod"` phía trên VẪN THẮNG mọi vai, kể cả vai đã
    // khoá. Đó là hành vi CỐ Ý (ban điều hành xem toàn cảnh), không phải lỗ
    // hổng lọt lại — đừng "sửa" nó ở đây. Cửa chặn thật của vai Điều Phối là
    // ma trận trang (`PAGE_ACCESS.coordinator = []`) và quyền từ máy chủ.
    case ROLE.Manager:
      return "manager";
    case ROLE.BranchManager:
      return "store";
    case ROLE.AreaManager:
      return "area";
    case ROLE.Sale:
      return "sale-staff";
  }

  if (!role && SALE_DEPTS.has(dept)) return "sale-staff";

  return "staff";
}

export function pickLayout(staff: Staff | null): DashboardLayout {
  return classify(staff);
}

export function canSeeFinance(staff: Staff | null): boolean {
  const layout = classify(staff);
  return layout === "full-access" || layout === "manager" || layout === "store";
}

export function canSeeMarketing(staff: Staff | null): boolean {
  const layout = classify(staff);
  return layout === "full-access" || layout === "sale-staff";
}

export function canSeeAllMetrics(staff: Staff | null): boolean {
  return classify(staff) === "full-access";
}

/**
 * Bản sao `auth.CompanyRevenueHidden` (backend/internal/platform/auth/data_visibility.go):
 * người qua được cửa trang đầu nhưng KHÔNG đọc số doanh thu — QC (8a,
 * 05/10/2026), vai Chỉ xem (CEO chốt Q14 17/10/2026), hai vai đã khoá, và
 * trưởng phòng kỹ thuật chụp / trang điểm / hậu kỳ hoặc chưa gán phòng (CEO chốt
 * Q03 17/10/2026, "chỉ việc phòng mình"). Máy chủ trả 0 ở ô tiền và từ chối
 * báo cáo doanh thu của họ, nên màn bỏ hẳn ô đó thay vì vẽ "0 ₫" như số thật.
 * `revenue-hidden.test.ts` đọc tệp Go và đỏ khi hai bản lệch nhau.
 */
export function isCompanyRevenueHidden(staff: Staff | null): boolean {
  if (!staff) return true;
  const role = norm(staff.role);
  if (role === ROLE.QC || role === ROLE.Viewer || role === ROLE.Coordinator || role === ROLE.ShiftLead) {
    return true;
  }
  if (role === ROLE.Manager) {
    const dept = norm(staff.department);
    return dept === "" || (TECHNICAL_BOARD_DEPARTMENTS as readonly string[]).includes(dept);
  }
  return false;
}
